<template>
  <tabs />
  <toolbar class="toolbar">
    <filter-pane v-model:page="page" v-model="filter" :filters="filters" />
    <add-fields-pane v-model="visibleColumns" :fields="columns" />
    <export
      url="/visibility_transfers/csv"
      :filter="filter"
      :columns="columns"
      :visible-columns="visibleColumns"
      :title="t(`csv.title.${ isOpenViewMode ? 'visibilityTransfers' : 'visibilityTransfersDone'}`)"
      :sort-key="sortKey"
      :sort-direction="sortDirection"
    />
  </toolbar>
  <visibility-transfer-modal
    v-model:open="openVisibilityTransferModal"
    v-model:visibility-transfer="editedVisibilityTransfer"
    @saved="refetch"
  />
  <visibility-transfer-confirmation-modal
    v-model:open="openVisibilityTransferConfirmationModal"
    v-model:visibility-transfer="editedVisibilityTransfer"
    @saved="refetch"
  />
  <div class="main-area h-full">
    <visibility-transfer-map
      v-if="records?.length"
      class="ml-12"
      :open-confirmation-modal="openConfirmationModalHandlerById"
      :busy="loading"
      :transfers="checkedRecords"
    />
    <div class="ml-12">
      <div class="text-body-regular-bold text-table-text-gray uppercase mb-5" data-v-67ba7ed1="">
        {{ t('pages.visibility.transfer.tableHeader') }}
      </div>

      <div class="table">
        <data-table
          v-model:sort-by="sortKey"
          v-model:sort-direction="sortDirection"
          v-model:checked-ids-list="checkedIdsList"
          class="mb-auto"
          :busy="loading"
          :items="records"
          :columns="columns"
          :visible-columns="visibleColumns"
          :actions="actions"
          :choosable="true"
        />
      </div>
    </div>
  </div>
  <pagination
    v-model:page="page"
    v-model:per-page="perPage"
    class="pagination"
    :total="total"
  />
</template>

<script lang="ts" setup>
import {
  computed, reactive, ref, unref,
} from 'vue';
import { useI18n } from 'vue-i18n';
import FilterPane from '@utils/toolbar/panes/Filter.vue';
import AddFieldsPane from '@utils/toolbar/panes/AddFields.vue';
import Toolbar from '@utils/toolbar/Toolbar.vue';
import Pagination from '@utils/table/Pagination.vue';
import DataTable, { type Column } from '@utils/table/Table.vue';
import { collOptionsProvider, type ProviderParams, useProvider } from '@utils/table/hooks/provider';
import type { Filter } from '@utils/filter/Filter.vue';
import Tabs from '@components/structure/navigation/Tabs.vue';
import VisibilityTransferModal from '@pages/visibility/visibilityTransfer/VisibilityTransferModal.vue';
import Export from '@utils/toolbar/panes/Export.vue';
import { format } from 'date-fns';
import { useTableFields } from '@hooks/table-configuration';
import { useQueryRef } from '@hooks/url';
import VisibilityTransferConfirmationModal
  from '@pages/visibility/visibilityTransfer/VisibilityTransferConfirmationModal.vue';
import {
  type Campaign,
  type HydraCollection,
  type Region,
  type VisibilityTransfer, VisibilityTransferMode,
} from '@/entities/Entities';
import VisibilityTransferMap from './VisibilityTransferMap.vue';
import router from '@/router';

interface Props {
  variant: string;
}
const props = defineProps<Props>();

const page = useQueryRef('page', 1);
const perPage = useQueryRef('perPage', 100);
const filter = useQueryRef('filter', {
  done: props.variant !== VisibilityTransferMode.OPEN,
});

const isOpenViewMode = computed(() => props.variant === VisibilityTransferMode.OPEN);

const openVisibilityTransferModal = ref(false);
const openVisibilityTransferConfirmationModal = ref(false);
const editedVisibilityTransfer = ref();

const sortKey = useQueryRef<string | undefined>('sortKey', 'transferTime');
const sortDirection = useQueryRef<'ASC' | 'DESC' | undefined>('sortDirection', 'ASC');

const { t } = useI18n();

const providerParams = reactive({
  sort: {
    sortKey,
    sortDirection,
  },
  filter,
  pagination: {
    page,
    perPage,
  },
});

const openConfirmationModalHandler = (item: VisibilityTransfer) => {
  openVisibilityTransferConfirmationModal.value = true;
  editedVisibilityTransfer.value = { ...item };
};

const openConfirmationModalHandlerById = (id: string) => {
  openConfirmationModalHandler(records.value?.find((record) => record.id === id) as VisibilityTransfer);
};

const actions = [
  ...(isOpenViewMode.value ? [{
    title: t('pages.visibility.transfer.actions.confirm'),
    action: openConfirmationModalHandler,
  }] : []),
  {
    title: t('pages.visibility.transfer.actions.edit'),
    action: (item: VisibilityTransfer) => {
      openVisibilityTransferModal.value = true;
      editedVisibilityTransfer.value = { ...item };
    },
  },
];

type ColumnFilter = Partial<Filter> & Omit<Filter, 'key'>;
type ColumnDefinition = Column & { filter?: ColumnFilter | ColumnFilter[] } & { group: string };

const columns: ColumnDefinition[] = [
  {
    key: 'id',
    title: t('id'),
    sortable: false,
    group: t('pages.visibility.fieldGroups.general'),
    class: 'whitespace-nowrap',
    filter: {
      type: 'text',
      group: t('pages.visibility.filterGroups.general'),
      options: {
        label: t('id'),
      },
    },
  },
  {
    key: 'visibilityPlanning.region.name',
    title: t('region'),
    group: t('pages.visibility.fieldGroups.general'),
    visible: true,
    filter: {
      key: 'visibilityPlanning.region',
      type: 'dropdown',
      group: t('pages.visibility.filterGroups.general'),
      options: {
        options: collOptionsProvider<Region>('/regions', {
          sort: {
            sortKey: 'name',
            sortDirection: 'ASC',
          },
        }, (value) => ({
          label: value.name,
          value: value.id,
        })),
        label: t('region'),
      },
    },
  },
  {
    key: 'transferTime',
    title: t('visibility.transfer.transferTime'),
    sortable: true,
    visible: true,
    formatter: (v: string) => (v ? format(new Date(v), 'dd.MM.yyyy - HH:mm:ss') : '-'),
    group: t('pages.visibility.filterGroups.general'),
    filter: {
      type: 'datetime',
      group: t('pages.visibility.filterGroups.general'),
      options: {
        label: t('visibility.transfer.transferTime'),
      },
    },
  },
  {
    key: 'actualTransferTime',
    title: t('visibility.transfer.actualTransferTime'),
    sortable: true,
    visible: true,
    formatter: (v: string) => (v ? format(new Date(v), 'dd.MM.yyyy - HH:mm:ss') : '-'),
    group: t('pages.visibility.filterGroups.general'),
    filter: {
      type: 'datetime',
      group: t('pages.visibility.filterGroups.general'),
      options: {
        label: t('visibility.transfer.actualTransferTime'),
      },
    },
  },
  {
    key: 'visibilityBicycle.number',
    title: t('pages.visibility.bicycle.number'),
    sortable: true,
    visible: true,
    group: t('pages.visibility.filterGroups.general'),
    to: (transfer: VisibilityTransfer) => ({
      name: 'bicycles',
      query: {
        filter: encodeURIComponent(JSON.stringify({
          id: transfer?.visibilityBicycle?.id,
        })),
      },
    }),
    filter: {
      type: 'range',
      group: t('pages.visibility.filterGroups.general'),
      options: {
        label: t('pages.visibility.bicycle.number'),
      },
    },
  },
  {
    key: 'visibilityPlanning.campaign.name',
    title: t('visibility.planning.campaign'),
    sortable: true,
    visible: true,
    group: t('pages.visibility.filterGroups.general'),
    to: (transfer: VisibilityTransfer) =>
      ({
        name: 'campaign-group-details',
        params: { campaignGroupId: transfer?.visibilityPlanning?.campaign?.group?.id },
        query: {
          globalFilter:
            transfer?.visibilityPlanning?.region?.id
              ? encodeURIComponent(JSON.stringify({ regions: [transfer?.visibilityPlanning?.region?.id] }))
              : undefined,
        },
      }),
    filter: {
      key: 'visibilityPlanning.campaign',
      type: 'dropdown',
      group: t('pages.visibility.filterGroups.general'),
      options: {
        label: t('visibility.campaign'),
        searchable: true,
        minChars: 2,
        filterResults: false,
        clearOnSelect: true,
        delay: 400,
        createOption: true,
        resolveOnLoad: true,
        options: (query: string, select: any) => {
          const input = query || select.modelValue;
          if (!input) return Promise.resolve([]);
          const filterQuery: ProviderParams = {
            filter: query ? { 'group.name': input } : { id: select.modelValue },
          };
          return collOptionsProvider<Campaign>('/campaigns', filterQuery, (value) => ({
            label: value.name,
            value: value.id,
          }))();
        },
      },
    },
  },
  {
    key: 'responsibleUser.fullName',
    title: t('visibility.transfer.responsibleUser'),
    sortable: true,
    visible: true,
    group: t('pages.visibility.filterGroups.general'),
    filter: {
      type: 'text',
      group: t('pages.visibility.filterGroups.general'),
      options: {
        label: t('visibility.transfer.responsibleUser'),
      },
    },
  },
  ...((props.variant === VisibilityTransferMode.DONE ? [{
    key: 'confirmationTime',
    title: t('visibility.transfer.confirmationTime'),
    sortable: true,
    visible: true,
    formatter: (v: string) => (v ? format(new Date(v), 'dd.MM.yyyy - HH:mm:ss') : '-'),
    group: t('pages.visibility.filterGroups.general'),
    filter: {
      type: 'datetime',
      group: t('pages.visibility.filterGroups.general'),
      options: {
        label: t('visibility.transfer.confirmationTime'),
      },
    },
  },
  {
    key: 'confirmedByUser.fullName',
    title: t('visibility.transfer.confirmedByUser'),
    sortable: true,
    visible: true,
    group: t('pages.visibility.filterGroups.general'),
    filter: {
      type: 'text',
      group: t('pages.visibility.filterGroups.general'),
      options: {
        label: t('visibility.transfer.confirmedByUser'),
      },
    },
  },
  ] : []) as ColumnDefinition[]),
  {
    key: 'oldLocation.name',
    title: t('visibility.transfer.oldLocation'),
    group: t('pages.visibility.fieldGroups.general'),
    visible: true,
    to: (transfer: VisibilityTransfer) => {
      const routeData = router.resolve({
        name: 'locations',
        query: {
          filter: encodeURIComponent(JSON.stringify({
            id: transfer?.oldLocation?.id,
          })),
        },
      });
      return routeData.href;
    },
    filter: {
      type: 'text',
      group: t('pages.visibility.filterGroups.general'),
      options: {
        label: t('visibility.transfer.oldLocation'),
      },
    },
  },
  {
    key: 'newLocation.name',
    title: t('visibility.transfer.newLocation'),
    group: t('pages.visibility.fieldGroups.general'),
    visible: true,
    to: (transfer: VisibilityTransfer) => {
      const routeData = router.resolve({
        name: 'locations',
        query: {
          filter: encodeURIComponent(JSON.stringify({
            id: transfer?.newLocation?.id,
          })),
        },
      });
      return routeData.href;
    },
    filter: {
      type: 'text',
      group: t('pages.visibility.filterGroups.general'),
      options: {
        label: t('visibility.transfer.newLocation'),
      },
    },
  },
  {
    key: 'lastTransferComment',
    title: t('visibility.transfer.lastTransferComment'),
    group: t('pages.visibility.fieldGroups.general'),
    visible: true,
    sortable: false,
  },
  {
    key: 'comment',
    title: t('visibility.transfer.comment'),
    group: t('pages.visibility.fieldGroups.general'),
    visible: true,
    filter: {
      type: 'text',
      group: t('pages.visibility.filterGroups.general'),
      options: {
        label: t('visibility.transfer.comment'),
      },
    },
  },
  {
    key: 'visibilityPlanning.confirmedByUser.fullName',
    title: t('visibility.transfer.createdByUser'),
    sortable: true,
    visible: true,
    group: t('pages.visibility.filterGroups.general'),
    filter: {
      type: 'text',
      group: t('pages.visibility.filterGroups.general'),
      options: {
        label: t('visibility.transfer.createdByUser'),
      },
    },
  },
  {
    key: 'updatedAt',
    title: t('updatedAt'),
    group: t('pages.visibility.filterGroups.general'),
    sortable: true,
    visible: true,
    formatter: (v: string) => (v ? format(new Date(v), 'dd.MM.yyyy - HH:mm:ss') : ''),
    filter: {
      type: 'datetime',
      group: t('pages.visibility.filterGroups.general'),
      options: {
        label: t('updatedAt'),
      },
    },
  },
  {
    key: 'createdAt',
    title: t('createdAt'),
    sortable: true,
    visible: true,
    formatter: (v: string) => (v ? format(new Date(v), 'dd.MM.yyyy - HH:mm:ss') : '-'),
    group: t('pages.visibility.filterGroups.general'),
    filter: {
      type: 'datetime',
      group: t('pages.visibility.filterGroups.general'),
      options: {
        label: t('createdAt'),
      },
    },
  },
];

const visibleColumns = useTableFields(columns);

const {
  data: items,
  loading: loadingTable,
  refetch,
} = useProvider<HydraCollection<VisibilityTransfer>>('/visibility_transfers', providerParams);

const records = computed(() => unref(items)?.['hydra:member']);
const total = computed(() => unref(items)?.['hydra:totalItems']);
const filters = computed(() =>
  columns.flatMap((col): Filter[] => {
    if (!col.filter) return [];
    if (Array.isArray(col.filter)) {
      return col.filter.map((f) => ({
        key: col.key,
        ...f,
      }));
    }
    return [{ key: col.key, ...col.filter }];
  }));

const loading = loadingTable; // computed(() => loadingTable.value || deleting.value);

const checkedIdsList = ref<string[]>([]);
const checkedRecords = computed(() => checkedIdsList.value.map((id) => records.value?.find((record) => record.id === id)));
</script>

<style lang="scss" scoped>
button {
  @apply block;
}

.table {
  @apply flex flex-col;
}
.medium {
  width: 300px;
  height: auto;
}
</style>

